import { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Table, VisuallyHidden } from '@zillow/constellation-10';

import GroupablePropertyRow from '../GroupablePropertyRow';

import { useSelector } from '../../../selectors/useSelector';
import { AppSelectors } from '../../../selectors';

import PropertyConstants from '../../../constants/PropertyConstants';
import PropertiesPageCSEvents from '../../../model/ClickstreamEvent/PropertiesPageCSEvents';
import { getCurrentMonthString } from '../../../utils/dateUtils';
import { FixedScrollbar } from '../../../containers/ZRAMPropertiesPage/components/PropertyTable/FixedScrollbar';
import { useTableObserver } from '../../../hooks/useTableObserver';

import mockGroupableListings from '../mocks/mockGroupableListings';

import { stickyLeftCellRecipe, TableHeaderCell } from '../GroupablePropertyRow/styles';

import { type Dispatch, type SetStateAction } from 'react';
import type { WebAnalyticsLeaseDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { ListingGroup } from '../../../types/GroupablePropertiesListDto';
import type { ZrmClickstreamListing } from '@zg-rentals/clickstream';

const { PROPERTY_TABLE_COLUMNS, PROPERTY_TABLE_TRIGGER_OBJECT_MAP } = PropertyConstants;

const GroupablePropertiesViewList = ({ listings }: { listings: Array<ListingGroup> }) => {
  const { user, isScreenMobile } = useSelector((state) => ({
    user: state.user,
    isScreenMobile: AppSelectors.makeIsScreenMobile(state),
  }));

  const tableRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [listingsState, setListingsState] = useState<Array<ListingGroup>>(listings);
  const [showMoreListingAlias, setShowMoreListingAlias] = useState('');
  const [hoveredCols, setHoveredCols] = useState<Record<string, boolean>>({});

  // track row indices currently animating (for add/remove transitions)
  const [animatingIndices, setAnimatingIndices] = useState<Set<number>>(new Set());
  const [removingIndices, setRemovingIndices] = useState<Set<number>>(new Set());

  const { isScrolledToLeft, showFixedScrollbar, isTableBottomVisible } = useTableObserver({
    scrollContainerRef,
    isScreenMobile,
  });

  const mockListingsArray = mockGroupableListings.singleListingArray;

  // returns animation style for rows being added (fade-in) or removed (fade-out)
  const getRowAnimationStyle = (index: number) => {
    if (animatingIndices.has(index)) {
      return { opacity: 0, animation: 'rowFadeIn 0.3s ease-out forwards' };
    }
    if (removingIndices.has(index)) {
      return { opacity: 1, animation: 'rowFadeOut 0.3s ease-out forwards' };
    }
    return undefined;
  };

  // triggers animation on indices, clears after duration, then runs optional callback
  const animateRows = (
    indices: Set<number>,
    setIndices: Dispatch<SetStateAction<Set<number>>>,
    duration: number,
    onComplete?: () => void,
  ) => {
    setIndices(indices);

    setTimeout(() => {
      setIndices(new Set());
      onComplete?.();
    }, duration);
  };

  const handleAddRow = ({ insertIndex }: { insertIndex: number }) => {
    const itemsToAdd = mockListingsArray;

    const indices = new Set(itemsToAdd.map((_, i) => insertIndex + i));
    animateRows(indices, setAnimatingIndices, 350);

    // update state immediately so rows exist in DOM to animate in
    setListingsState((prev) => {
      const newListings = [...prev];
      newListings.splice(insertIndex, 0, ...itemsToAdd);

      return newListings;
    });
  };

  const handleRemoveRow = ({ removeStartIndex }: { removeStartIndex: number }) => {
    if (listingsState.length <= 1) return;

    const removeCount = Math.min(mockListingsArray.length, listingsState.length - removeStartIndex);

    const indices = new Set(Array.from({ length: removeCount }, (_, i) => removeStartIndex + i));
    // delay state update until after animation so rows stay in DOM to animate out
    animateRows(indices, setRemovingIndices, 300, () => {
      setListingsState((prev) => {
        const newListings = [...prev];
        newListings.splice(removeStartIndex, removeCount);

        return newListings;
      });
    });
  };

  const closeOpenMenus = useCallback(
    (e: MouseEvent | KeyboardEvent) => {
      if (tableRef.current && showMoreListingAlias && !tableRef.current.contains(e.target as Node)) {
        setTimeout(() => {
          setShowMoreListingAlias('');
        }, 0);
      }
    },
    [showMoreListingAlias],
  );

  useEffect(() => {
    document.addEventListener('mouseup', closeOpenMenus);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeOpenMenus(e);
      }
    });

    return () => {
      document.removeEventListener('mouseup', closeOpenMenus);
      document.removeEventListener('keydown', closeOpenMenus);
    };
  }, [closeOpenMenus]);

  const handleUpsellCellHover = (
    columnLabel: string,
    currentProperty: ZrmClickstreamListing,
    lease?: WebAnalyticsLeaseDto,
  ) => {
    // only fire the cs event if we have not already fired one for an cell in this column
    if (!hoveredCols[columnLabel]) {
      PropertiesPageCSEvents.PropertyCardHover({
        user,
        currentProperty,
        lease,
        triggerObject:
          PROPERTY_TABLE_TRIGGER_OBJECT_MAP.HOVER_TRIGGERS[
            columnLabel as keyof typeof PROPERTY_TABLE_TRIGGER_OBJECT_MAP.HOVER_TRIGGERS
          ],
      });
      setHoveredCols((prev) => ({ ...prev, [columnLabel]: true }));
    }
  };

  return (
    <Box
      ref={tableRef}
      css={{
        overflow: { base: 'auto', lg: 'hidden' },
        borderRadius: '200',
        borderBottomRadius: '0',
        borderLeft: '1px solid {colors.Table.Cell.border}',
        borderRight: '1px solid {colors.Table.Cell.border}',
        borderTop: '1px solid {colors.border.subtle}',
      }}
    >
      <Box
        ref={scrollContainerRef}
        css={{
          overflowX: { base: 'hidden', lg: 'auto' },
          maxWidth: '100%',
        }}
      >
        <Table.Root
          appearance="horizontal"
          aria-label="Medium-sized grid table"
          size="md"
          data-testid="GroupablePropertiesViewList"
          css={{ tableLayout: 'fixed', minWidth: { lg: '1550px' } }}
        >
          <Table.Header css={{ display: { base: 'none', lg: 'table-header-group' } }}>
            <Table.Row>
              <TableHeaderCell
                id={PROPERTY_TABLE_COLUMNS.ADDRESS}
                css={stickyLeftCellRecipe.raw({ scrolled: isScrolledToLeft })}
              >
                Address
              </TableHeaderCell>
              <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.RENT}>
                {getCurrentMonthString(new Date())} rent
              </TableHeaderCell>
              <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.POTENTIAL_TENANTS}>Potential tenants</TableHeaderCell>
              <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.LEASES}>Lease</TableHeaderCell>
              <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.ACTIONS}>
                <VisuallyHidden>Actions</VisuallyHidden>
              </TableHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {listingsState.map((listing, index) => (
              <GroupablePropertyRow
                key={index}
                index={index}
                listing={listing}
                showMoreListingAlias={showMoreListingAlias}
                setShowMoreListingAlias={setShowMoreListingAlias}
                onUpsellCellHover={handleUpsellCellHover}
                rowStyle={getRowAnimationStyle(index)}
                isScrolledToLeft={isScrolledToLeft}
              />
            ))}
          </Table.Body>
        </Table.Root>
      </Box>

      <FixedScrollbar
        scrollContainerRef={scrollContainerRef}
        isVisible={showFixedScrollbar}
        isTableBottomVisible={isTableBottomVisible}
      />
    </Box>
  );
};

export { GroupablePropertiesViewList };
export default GroupablePropertiesViewList;
